import React, { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Spin } from 'antd'
import MainLayout from '@/layouts/MainLayout'
import PrivateRoute from './PrivateRoute'

// lazy import — 초기 번들 크기 최소화
const LoginPage            = lazy(() => import('@/features/auth/LoginPage'))
const GameListPage         = lazy(() => import('@/features/games/GameListPage'))
const GameDetailPage       = lazy(() => import('@/features/games/GameDetailPage'))
const GameResultPage       = lazy(() => import('@/features/games/GameResultPage'))
const GameCreatePage       = lazy(() => import('@/features/games/GameCreatePage'))
const RecordListPage       = lazy(() => import('@/features/records/RecordListPage'))
const RecordInputPage      = lazy(() => import('@/features/records/RecordInputPage'))
const VerifyPage           = lazy(() => import('@/features/verify/VerifyPage'))
const StatsPage            = lazy(() => import('@/features/stats/StatsPage'))
const PlayerListPage       = lazy(() => import('@/features/players/PlayerListPage'))
const PlayerDetailPage     = lazy(() => import('@/features/players/PlayerDetailPage'))
const DataManagementPage   = lazy(() => import('@/features/data/DataManagementPage'))
const TrainingVideoPage    = lazy(() => import('@/features/videos/TrainingVideoPage'))
const EstimateGroupPage    = lazy(() => import('@/features/estimate/EstimateGroupPage'))
const EstimateSettingPage  = lazy(() => import('@/features/estimate/EstimateSettingPage'))
const EstimateResultPage   = lazy(() => import('@/features/estimate/EstimateResultPage'))
const AgreesPage           = lazy(() => import('@/features/agrees/AgreesPage'))
const StatSalaryPage       = lazy(() => import('@/features/statSalary/StatSalaryPage'))
const BatchPage            = lazy(() => import('@/features/data/BatchPage'))
const NotFoundPage         = lazy(() => import('@/features/common/NotFoundPage'))
const UnauthorizedPage     = lazy(() => import('@/features/common/UnauthorizedPage'))

const Loading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
    <Spin size="large" />
  </div>
)

const AppRouter: React.FC = () => (
  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* 공개 라우트 */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/not-available" element={<div style={{ padding: 40 }}>이 도메인에서 사용할 수 없는 기능입니다.</div>} />
        <Route path="/unauthorized"  element={<UnauthorizedPage />} />

        {/* 인증 필요 라우트 */}
        <Route
          element={
            <PrivateRoute>
              <MainLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/games" replace />} />

          {/* 경기 관리 — 모든 인증 사용자 접근 가능 */}
          <Route path="/games" element={<GameListPage />} />
          <Route path="/games/:gameInfoId/result" element={<GameResultPage />} />
          <Route path="/games/:gameInfoId" element={<GameDetailPage />} />
          <Route
            path="/games/new"
            element={
              <PrivateRoute maxUserLevel={4}>
                <GameCreatePage />
              </PrivateRoute>
            }
          />

          {/* 기록 입력 — 레벨 4 이하 */}
          <Route
            path="/recorder"
            element={
              <PrivateRoute maxUserLevel={4}>
                <RecordInputPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/recorder/:gameInfoId"
            element={
              <PrivateRoute maxUserLevel={4}>
                <RecordInputPage />
              </PrivateRoute>
            }
          />

          {/* 기록 조회 */}
          <Route path="/records/:gameInfoId" element={<RecordListPage />} />

          {/* 검증 */}
          <Route
            path="/verify"
            element={
              <PrivateRoute maxUserLevel={4}>
                <VerifyPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/verify/:gameInfoId"
            element={
              <PrivateRoute maxUserLevel={4}>
                <VerifyPage />
              </PrivateRoute>
            }
          />

          {/* 통계 — 레벨 5 이하, stat 모듈 필요 */}
          <Route
            path="/stats/*"
            element={
              <PrivateRoute maxUserLevel={5} requiredModule="stat">
                <StatsPage />
              </PrivateRoute>
            }
          />

          {/* 선수 관리 — 레벨 4 이하, player 모듈 필요 */}
          <Route
            path="/players"
            element={
              <PrivateRoute maxUserLevel={4} requiredModule="player">
                <PlayerListPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/players/:memberId"
            element={
              <PrivateRoute maxUserLevel={4} requiredModule="player">
                <PlayerDetailPage />
              </PrivateRoute>
            }
          />

          {/* 훈련영상 관리 — 레벨 4 이하 */}
          <Route
            path="/training-videos"
            element={
              <PrivateRoute maxUserLevel={4}>
                <TrainingVideoPage />
              </PrivateRoute>
            }
          />

          {/* 평가 관리 — 레벨 4 이하, estimate 모듈 필요 */}
          <Route
            path="/estimate/groups"
            element={
              <PrivateRoute maxUserLevel={4} requiredModule="estimate">
                <EstimateGroupPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/estimate/settings"
            element={
              <PrivateRoute maxUserLevel={4} requiredModule="estimate">
                <EstimateSettingPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/estimate/results"
            element={
              <PrivateRoute maxUserLevel={5} requiredModule="estimate">
                <EstimateResultPage />
              </PrivateRoute>
            }
          />

          {/* 연봉협상 협의 — 레벨 5 이하, estimate 모듈 필요 */}
          <Route
            path="/agrees"
            element={
              <PrivateRoute maxUserLevel={5} requiredModule="estimate">
                <AgreesPage />
              </PrivateRoute>
            }
          />

          {/* Stat&연봉 비교 — 레벨 5 이하, estimate 모듈 필요 */}
          <Route
            path="/stat-salary"
            element={
              <PrivateRoute maxUserLevel={5} requiredModule="estimate">
                <StatSalaryPage />
              </PrivateRoute>
            }
          />

          {/* 배치 작업 — 최고관리자(레벨 0)만 */}
          <Route
            path="/batch"
            element={
              <PrivateRoute maxUserLevel={0} requiredModule="config">
                <BatchPage />
              </PrivateRoute>
            }
          />

          {/* 데이터 관리 — 최고관리자(레벨 0)만 */}
          <Route
            path="/data"
            element={
              <PrivateRoute maxUserLevel={0}>
                <DataManagementPage />
              </PrivateRoute>
            }
          />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  </BrowserRouter>
)

export default AppRouter
